// Login por biometria (WebAuthn/passkeys) — cliente.
// O aparelho usa o autenticador local (digital/Face ID); o servidor guarda
// só a chave pública e verifica as assinaturas. Mesma política do app
// original: o botão de login biométrico só aparece quando o aparelho
// suporta E o usuário ativou NESTE aparelho (flag local); o logout NÃO
// remove a flag — só o token.
import {
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";
import { apiFetch } from "@/lib/api";
import type { User } from "@/types";

const ENABLED_KEY = "bolao-pga/biometrics-enabled";

/** O usuário ativou biometria neste aparelho (não removida no logout). */
export function biometricsMarkedEnabled(): boolean {
  return localStorage.getItem(ENABLED_KEY) === "1";
}

/** Aparelho tem autenticador de plataforma (digital/Face ID) disponível. */
export async function biometricsSupported(): Promise<boolean> {
  if (!("PublicKeyCredential" in window)) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/** Registra a credencial do aparelho para a conta logada. */
export async function enableBiometrics(): Promise<void> {
  const options = await apiFetch<PublicKeyCredentialCreationOptionsJSON>(
    "/api/me/webauthn/register-options",
    { method: "POST" },
  );
  const attestation = await startRegistration({ optionsJSON: options });
  await apiFetch("/api/me/webauthn/register", {
    method: "POST",
    body: JSON.stringify(attestation),
  });
  localStorage.setItem(ENABLED_KEY, "1");
}

/** Remove TODAS as credenciais da conta (server) + flag local. */
export async function disableBiometrics(): Promise<void> {
  await apiFetch("/api/me/webauthn", { method: "DELETE" });
  localStorage.removeItem(ENABLED_KEY);
}

/** Estado no servidor (a conta tem credenciais registradas?). */
export async function biometricsServerEnabled(): Promise<boolean> {
  const res = await apiFetch<{ enabled: boolean }>("/api/me/webauthn");
  return res.enabled;
}

/** Erro de cancelamento/timeout do prompt nativo — não é falha real. */
export function isWebauthnCancel(err: unknown): boolean {
  return err instanceof DOMException && err.name === "NotAllowedError";
}

/**
 * Login por passkey (credencial discoverable — o navegador identifica o
 * usuário, sem digitar e-mail). Retorna o mesmo payload do /auth/login.
 */
export async function biometricLoginRequest(): Promise<{
  token: string;
  user: User;
}> {
  const { sessionId, options } = await apiFetch<{
    sessionId: string;
    options: PublicKeyCredentialRequestOptionsJSON;
  }>("/api/auth/webauthn/login-options", { method: "POST", auth: false });
  const assertion = await startAuthentication({ optionsJSON: options });
  return apiFetch<{ token: string; user: User }>("/api/auth/webauthn/login", {
    method: "POST",
    auth: false,
    body: JSON.stringify({ sessionId, response: assertion }),
  });
}
